/* finger-chooser/render.js — draws one frame of the play field on a 2D
 * canvas from the round's view(). Stateless except for a cache of the team
 * tint; no DOM beyond the context it is handed.
 *
 * Two things shape every decision here:
 *   • The finger COVERS the centre of its ring. Anything a player must read
 *     (a shape, a place number) goes on a badge beside the ring — on the side
 *     facing the middle of the screen, because the hand comes in from the
 *     player's own edge. The badge text is turned to face that edge, so on a
 *     phone lying flat among four people everyone reads their own number.
 *   • The phone is looked at from arm's length across a table. The result
 *     has to be readable from there: the winner's colour floods the screen
 *     with a hole around the winning finger; teams tint the whole screen by
 *     region; the turn order is drawn as a path from 1 to 2 to 3.
 */

import { SHAPES, finger, team, rgba } from './palette.js';
import { hopAt } from './round.js';

const TAU = Math.PI * 2;
const pathCache = new Map();
function shapePath(name) {
    let p = pathCache.get(name);
    if (!p) {
        try { p = new Path2D(SHAPES[name] || SHAPES.circle); } catch (_) { p = null; }
        pathCache.set(name, p);
    }
    return p;
}

export function ringRadius(W, H) {
    return Math.max(38, Math.min(66, Math.min(W, H) * 0.105));
}

/* Where a finger's badge goes and which way its text faces. */
export function badgeGeom(x, y, W, H, R) {
    let dx = W / 2 - x, dy = H / 2 - y;
    let d = Math.hypot(dx, dy);
    if (d < R * 0.5) { dx = 0; dy = -1; d = 1; }
    const ux = dx / d, uy = dy / d;
    const dist = R * 1.55;
    // The player sits at the edge nearest to the finger: text «down» points
    // there. Only when the finger is clearly near that edge — a finger in the
    // middle says nothing about where its owner sits, and upright text is the
    // right guess for a phone held in the hand.
    const near = Math.min(W, H) * 0.16;
    const edges = [
        { e: 'bottom', d: H - y, rot: 0 },
        { e: 'top', d: y, rot: Math.PI },
        { e: 'left', d: x, rot: Math.PI / 2 },
        { e: 'right', d: W - x, rot: -Math.PI / 2 },
    ];
    edges.sort((a, b) => a.d - b.d);
    const pick = edges[0].d <= near ? edges[0] : edges.find((q) => q.e === 'bottom');
    return { bx: x + ux * dist, by: y + uy * dist, ux, uy, rot: pick.rot, edge: pick.e };
}

function drawShape(ctx, name, x, y, size, color) {
    const p = shapePath(name);
    ctx.save();
    ctx.translate(x, y);
    const s = size / 24;
    ctx.scale(s, s);
    ctx.translate(-12, -12);
    ctx.fillStyle = color;
    if (p) ctx.fill(p);
    else { ctx.beginPath(); ctx.arc(12, 12, 9, 0, TAU); ctx.fill(); }
    ctx.restore();
}

function badge(ctx, g, sw, R, content, opts) {
    opts = opts || {};
    const rb = R * (opts.big ? 0.5 : 0.36) * (opts.pop || 1);
    ctx.save();
    ctx.globalAlpha *= opts.alpha == null ? 1 : opts.alpha;
    ctx.beginPath();
    ctx.arc(g.bx, g.by, rb, 0, TAU);
    ctx.fillStyle = sw.hex;
    ctx.fill();
    ctx.lineWidth = Math.max(2, R * 0.05);
    ctx.strokeStyle = 'rgba(0,0,0,.55)';
    ctx.stroke();
    if (content.shape) drawShape(ctx, content.shape, g.bx, g.by, rb * 1.25, sw.ink);
    if (content.text != null) {
        ctx.translate(g.bx, g.by);
        ctx.rotate(g.rot);
        ctx.fillStyle = sw.ink;
        const fs = Math.round(rb * (String(content.text).length > 1 ? 1.05 : 1.25));
        ctx.font = '800 ' + fs + 'px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(content.text), 0, fs * 0.05);
    }
    ctx.restore();
}

/* One ring. `o`: alpha, scale, progress (0–1 or null), spin (radians or null),
 * lit (bool), thin (bool). */
function ring(ctx, x, y, R, hex, o) {
    const alpha = o.alpha == null ? 1 : o.alpha;
    const r = R * (o.scale || 1);
    ctx.save();
    ctx.globalAlpha = alpha;
    // halo
    const g = ctx.createRadialGradient(x, y, r * 0.35, x, y, r * (o.lit ? 2.3 : 1.9));
    g.addColorStop(0, rgba(hex, o.lit ? 0.55 : 0.34));
    g.addColorStop(1, rgba(hex, 0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r * (o.lit ? 2.3 : 1.9), 0, TAU);
    ctx.fill();
    // body
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fillStyle = rgba(hex, o.thin ? 0.08 : 0.2);
    ctx.fill();
    ctx.lineWidth = o.thin ? Math.max(2, r * 0.06) : Math.max(5, r * 0.17);
    ctx.strokeStyle = hex;
    ctx.stroke();
    if (o.lit) {
        ctx.lineWidth = Math.max(2, r * 0.05);
        ctx.strokeStyle = 'rgba(255,255,255,.9)';
        ctx.beginPath();
        ctx.arc(x, y, r * 1.18, 0, TAU);
        ctx.stroke();
    }
    // countdown: the ring outside fills clockwise from 12 o'clock
    if (o.progress != null) {
        const rr = r * 1.32;
        ctx.lineWidth = Math.max(4, r * 0.1);
        ctx.strokeStyle = 'rgba(255,255,255,.16)';
        ctx.beginPath();
        ctx.arc(x, y, rr, 0, TAU);
        ctx.stroke();
        if (o.progress > 0) {
            ctx.strokeStyle = hex;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.arc(x, y, rr, -Math.PI / 2, -Math.PI / 2 + TAU * o.progress);
            ctx.stroke();
        }
    }
    // «waiting» — three arcs chasing each other round the ring
    if (o.spin != null) {
        const rr = r * 1.32;
        ctx.lineWidth = Math.max(3, r * 0.07);
        ctx.strokeStyle = rgba(hex, 0.8);
        ctx.lineCap = 'round';
        for (let k = 0; k < 3; k++) {
            const a0 = o.spin + (k * TAU) / 3;
            ctx.beginPath();
            ctx.arc(x, y, rr, a0, a0 + 0.75);
            ctx.stroke();
        }
    }
    ctx.restore();
}

function label(ctx, g, x, y, R, text, color, shadow) {
    ctx.save();
    const lx = x + g.ux * R * 2.9, ly = y + g.uy * R * 2.9;
    ctx.translate(lx, ly);
    ctx.rotate(g.rot);
    const fs = Math.round(Math.max(18, R * 0.5));
    ctx.font = '800 ' + fs + 'px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (shadow) { ctx.shadowColor = shadow; ctx.shadowBlur = 8; }
    ctx.fillStyle = color;
    ctx.fillText(text, 0, 0);
    ctx.restore();
}

/* Team regions: every point of the field tinted by the team of the nearest
 * finger, on a coarse grid scaled up smoothly. Cached per result and size. */
let tintCache = { key: '', canvas: null };
function teamTint(ctx, W, H, result) {
    const key = result.round + ':' + W + 'x' + H;
    if (tintCache.key !== key) {
        // 3 CSS px per cell: the borders between regions read as lines, not
        // stairs; a 360×600 field is 24 000 cells, computed once per result.
        const cell = 3;
        const cw = Math.max(1, Math.ceil(W / cell)), ch = Math.max(1, Math.ceil(H / cell));
        let off = null;
        try {
            off = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(cw, ch) : document.createElement('canvas');
            off.width = cw; off.height = ch;
        } catch (_) { off = null; }
        if (!off) return;
        const octx = off.getContext('2d');
        const img = octx.createImageData(cw, ch);
        const pts = result.ids.map((id) => {
            const p = result.at[id];
            const t = team(result.teamOf.get(id));
            const n = parseInt(t.hex.slice(1), 16);
            return { x: p.x, y: p.y, r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
        });
        for (let j = 0; j < ch; j++) {
            for (let i = 0; i < cw; i++) {
                const px = (i + 0.5) * cell, py = (j + 0.5) * cell;
                let best = 0, bd = Infinity;
                for (let k = 0; k < pts.length; k++) {
                    const d = (pts[k].x - px) * (pts[k].x - px) + (pts[k].y - py) * (pts[k].y - py);
                    if (d < bd) { bd = d; best = k; }
                }
                const o = (j * cw + i) * 4;
                img.data[o] = pts[best].r; img.data[o + 1] = pts[best].g; img.data[o + 2] = pts[best].b; img.data[o + 3] = 255;
            }
        }
        octx.putImageData(img, 0, 0);
        tintCache = { key, canvas: off };
    }
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.globalAlpha = 0.3;
    ctx.drawImage(tintCache.canvas, 0, 0, W, H);
    ctx.restore();
}

function orderPath(ctx, result, pos, R, reveal) {
    const byPlace = result.ids.slice().sort((a, b) => result.placeOf.get(a) - result.placeOf.get(b));
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,.5)';
    ctx.fillStyle = 'rgba(255,255,255,.7)';
    ctx.lineWidth = Math.max(2, R * 0.06);
    ctx.setLineDash([R * 0.2, R * 0.16]);
    for (let i = 0; i + 1 < byPlace.length; i++) {
        if (reveal != null && result.placeOf.get(byPlace[i + 1]) > reveal) break;
        const a = pos(byPlace[i]), b = pos(byPlace[i + 1]);
        const dx = b.x - a.x, dy = b.y - a.y, d = Math.hypot(dx, dy);
        if (d < R * 2.6) continue;
        const ux = dx / d, uy = dy / d;
        const x0 = a.x + ux * R * 1.4, y0 = a.y + uy * R * 1.4, x1 = b.x - ux * R * 1.4, y1 = b.y - uy * R * 1.4;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
        // arrowhead at the middle of the segment
        const mx = (x0 + x1) / 2, my = (y0 + y1) / 2, s = R * 0.22;
        ctx.save();
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(mx + ux * s, my + uy * s);
        ctx.lineTo(mx - ux * s - uy * s * 0.8, my - uy * s + ux * s * 0.8);
        ctx.lineTo(mx - ux * s + uy * s * 0.8, my - uy * s - ux * s * 0.8);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
    }
    ctx.restore();
}

/* Place reveal times inside the suspense (ms from its start) for order mode. */
export function revealTimes(n, suspenseMs) {
    const out = [];
    const a = suspenseMs * 0.08, span = suspenseMs * 0.8;
    for (let p = 1; p <= n; p++) out.push(Math.round(a + (n > 1 ? (span * (p - 1)) / (n - 1) : 0)));
    return out;
}

/**
 * Draw a frame.
 *   view  — round.view(now)
 *   t     — animation time in ms (for spinners)
 *   env   — { reduced, labels: { winner, loser }, cancelFlash }
 */
export function drawFrame(ctx, W, H, view, t, env) {
    env = env || {};
    ctx.clearRect(0, 0, W, H);
    const R = ringRadius(W, H);
    const res = view.result;
    const phase = view.phase;
    const reduced = !!env.reduced;
    const byId = new Map(view.fingers.map((f) => [f.id, f]));
    const pos = (id) => {
        const f = byId.get(id);
        if (f) return f;
        return (res && res.at[id]) || { x: W / 2, y: H / 2 };
    };

    const inDraw = res && (phase === 'suspense' || phase === 'result' || phase === 'shown');
    if (!inDraw) {
        // Before the draw: every finger its own ring.
        for (const f of view.fingers) {
            if (f.state === 'late') continue;
            const sw = finger(f.slot);
            const breathe = reduced ? 1 : 1 + 0.035 * Math.sin(t / 260 + f.slot);
            const counting = phase === 'counting';
            ring(ctx, f.x, f.y, R, sw.hex, {
                scale: breathe,
                progress: counting ? view.progress : null,
                spin: !counting ? (reduced ? 0 : t / 420) : null,
            });
            badge(ctx, badgeGeom(f.x, f.y, W, H, R), sw, R, { shape: sw.shape });
        }
        return;
    }

    const mode = res.mode;
    const ts = phase === 'suspense' ? view.suspenseT : view.suspenseMs;
    const S = view.suspenseMs || 1;
    const fadeIn = phase === 'suspense' ? 0 : Math.min(1, (view.sinceResult || 0) / (reduced ? 1 : 320));
    const aliveAlpha = (id) => {
        const f = byId.get(id);
        return f && f.state === 'lifted' ? 0.6 : 1;
    };

    if (mode === 'winner' || mode === 'loser') {
        const k = res.chosenOrder.length;
        const landed = ts >= (res.hops ? res.hops.landAt : 0);
        let litId = null;
        if (phase === 'suspense' && res.hops && !landed) litId = res.ring[res.hops.hops[hopAt(res.hops, ts)].pos];

        // Flood: the chosen colour (or a dark veil for several) with holes.
        if (phase !== 'suspense' || landed) {
            const a = phase === 'suspense' ? 0.25 : 0.25 + 0.67 * fadeIn;
            const first = finger(res.at[res.chosenOrder[0]].slot);
            const floodHex = k === 1 ? first.hex : '#070a16';
            ctx.save();
            ctx.beginPath();
            ctx.rect(0, 0, W, H);
            for (const id of res.chosenOrder) {
                const p = pos(id);
                ctx.moveTo(p.x + R * 1.9, p.y);
                ctx.arc(p.x, p.y, R * 1.9, 0, TAU);
            }
            ctx.fillStyle = rgba(floodHex, k === 1 ? a : Math.min(0.86, a));
            ctx.fill('evenodd');
            ctx.restore();
        }
        for (const id of res.ids) {
            const p = pos(id);
            const sw = finger(res.at[id].slot);
            const chosen = res.chosen.has(id);
            let o;
            if (phase === 'suspense' && !landed) {
                o = id === litId ? { lit: true, scale: 1.14 } : { alpha: 0.35, thin: true };
            } else if (chosen) {
                const blink = phase === 'suspense' && !reduced ? 0.7 + 0.3 * Math.abs(Math.sin(ts / 60)) : 1;
                o = { lit: true, scale: 1.1, alpha: blink * aliveAlpha(id) };
            } else {
                o = { alpha: phase === 'suspense' ? 0.35 : Math.max(0.12, 0.35 * (1 - fadeIn)), thin: true };
            }
            ring(ctx, p.x, p.y, R, sw.hex, o);
            const g = badgeGeom(p.x, p.y, W, H, R);
            if (chosen && (phase !== 'suspense' || landed)) {
                badge(ctx, g, sw, R, { shape: sw.shape }, { big: true });
                if (phase !== 'suspense') {
                    const txt = mode === 'winner' ? env.labels.winner : env.labels.loser;
                    const on = k === 1 ? finger(res.at[res.chosenOrder[0]].slot) : null;
                    label(ctx, g, p.x, p.y, R, txt, on ? on.ink : '#ffffff', on ? null : 'rgba(0,0,0,.7)');
                }
            } else if (phase === 'suspense' && !landed) {
                badge(ctx, g, sw, R, { shape: sw.shape }, { alpha: id === litId ? 1 : 0.4 });
            }
        }
    } else if (mode === 'teams') {
        const settleAt = S * 0.72;
        const settled = phase !== 'suspense' || ts >= settleAt;
        if (settled) teamTint(ctx, W, H, res);
        res.ids.forEach((id, i) => {
            const p = pos(id);
            let tIdx = res.teamOf.get(id);
            if (!settled) tIdx = (Math.floor(ts / 120) + i) % res.count;
            const sw = team(tIdx);
            ring(ctx, p.x, p.y, R, sw.hex, { lit: settled, alpha: aliveAlpha(id), scale: settled ? 1.06 : 1 });
            badge(ctx, badgeGeom(p.x, p.y, W, H, R), sw, R, { shape: sw.shape }, { big: settled });
        });
    } else if (mode === 'order') {
        const times = revealTimes(res.ids.length, S);
        const revealed = (place) => phase !== 'suspense' || ts >= times[place - 1];
        let lastRevealed = 0;
        for (let p = 1; p <= res.ids.length; p++) if (revealed(p)) lastRevealed = p;
        if (lastRevealed >= 2) orderPath(ctx, res, pos, R, lastRevealed);
        for (const id of res.ids) {
            const p = pos(id);
            const sw = finger(res.at[id].slot);
            const place = res.placeOf.get(id);
            const shown = revealed(place);
            const first = place === 1 && shown;
            ring(ctx, p.x, p.y, R, sw.hex, { lit: first, alpha: (shown ? 1 : 0.4) * aliveAlpha(id), scale: first ? 1.1 : 1 });
            const g = badgeGeom(p.x, p.y, W, H, R);
            if (shown) {
                let pop = 1;
                if (!reduced && phase === 'suspense') {
                    const since = ts - times[place - 1];
                    if (since < 200) pop = 1 + 0.45 * (1 - since / 200);
                }
                badge(ctx, g, sw, R, { text: place }, { big: true, pop });
            } else {
                badge(ctx, g, sw, R, { shape: sw.shape }, { alpha: 0.4 });
            }
        }
    }

    // Late fingers: a small grey ring — seen, but not in this round.
    for (const f of view.fingers) {
        if (f.state !== 'late') continue;
        ring(ctx, f.x, f.y, R * 0.55, '#9ca3af', { alpha: 0.6, thin: true });
    }
}
